import React, { useCallback, useMemo } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { ErrorView } from "components/common/ErrorView";
import { ErrorType } from "components/common/ErrorView/types";
import { getConfig } from "config/config";
import { User } from "types/users";

import { CustomRoutesContainer } from "./CustomRoutesContainer";
import { PROTECTED_ROUTE_SPECS, PUBLIC_ROUTE_SPECS, RouteSpec } from "./routeSpecs";


const { REACT_APP_BASE_PREFIX } = getConfig();

interface CustomRoutesProps {
  user: User | undefined;
  setRouteSpec: (spec: RouteSpec) => void;
  errorTypeForView: ErrorType | null;
}

export function CustomRoutes({ user, setRouteSpec, errorTypeForView }: CustomRoutesProps) {
  const routeIsAccessible = useCallback((routeSpec: RouteSpec, user?: User) => {
    // TODO: better authz permissions so we don't have long and confusing boolean chains like this
    if (routeSpec.admin) {
      return user?.is_superuser; // Block non-admin users from seeing admin pages
    }
    if (routeSpec.only_labeler_can_view) {
      return user?.only_labeler_ui; // Only admins and labelers
    }
    if (routeSpec.labeler_can_view) {
      return !!user; // Any logged in user (ensure boolean)
    }
    if (routeSpec.path === "/disagreements") {
      return user?.show_disagreements_management;
    }
    if (routeSpec.path === "/labelers-management") {
      return user?.show_labelers_management;
    }
    if (routeSpec.path === "/disagreements-detail") {
      return user?.show_disagreements_management;
    }
    return !user?.only_labeler_ui; // Any non-labeler
  }, []);

  const finalRoutes = useMemo(() => {
    // PUBLIC ROUTES
    const publicRoutes: Array<JSX.Element> = [
      <Route key="root-redirect" path="/" element={<Navigate to={REACT_APP_BASE_PREFIX} replace />} />,
    ];

    PUBLIC_ROUTE_SPECS.forEach((routeSpec) => {
      const element = React.createElement(routeSpec.component as React.ElementType);
      publicRoutes.push(<Route path={`${REACT_APP_BASE_PREFIX}${routeSpec.path}`} key={routeSpec.name} element={element} />);
    });

    // PROTECTED ROUTES
    const protectedRoutes: JSX.Element[] = [];
    if (user) {
      PROTECTED_ROUTE_SPECS.forEach((spec) => {
        if (routeIsAccessible(spec, user)) {
          // Main path
          protectedRoutes.push(
            <Route
              id={`${spec.name}-route`}
              path={`${REACT_APP_BASE_PREFIX}${spec.path}`}
              key={spec.name}
              element={
                <CustomRoutesContainer routeSpec={spec} setRouteSpec={setRouteSpec} user={user}>
                  <spec.component user={user} />
                </CustomRoutesContainer>
              }
            />,
          );

          // Add path aliases too
          spec.pathAliases?.forEach((pathAlias) => {
            protectedRoutes.push(
              <Route
                id={`${spec.name}-route-alias-${pathAlias}`}
                path={`${REACT_APP_BASE_PREFIX}${pathAlias}`}
                key={`${spec.name}-alias-${pathAlias}`}
                element={
                  <CustomRoutesContainer routeSpec={spec} setRouteSpec={setRouteSpec} user={user}>
                    <spec.component user={user} />
                  </CustomRoutesContainer>
                }
              />,
            );
          });
        }
      });
    }

    const notFoundRoute = <Route path="*" key="not-found" element={<ErrorView error={ErrorType.ERROR_404} />} />;
    return [...publicRoutes, ...protectedRoutes, notFoundRoute];
  }, [user, routeIsAccessible, setRouteSpec]);

  return (
    <Routes>
      {finalRoutes}
      {errorTypeForView && !user && (
        <Route path={REACT_APP_BASE_PREFIX} element={<ErrorView error={errorTypeForView} />} />
      )}
    </Routes>
  );
}
